/**
 * API client for auth endpoints with automatic token refresh.
 *
 * Features:
 * - Automatic Authorization header injection
 * - Token refresh before expiry
 * - Retry with exponential backoff for 5xx errors
 * - Correlation ID tracking
 */
import { useAuthStore } from '../store';
import { getCurrentSession, getTokensFromSession } from './cognito-auth';
import { getAuthConfig } from '../config';
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
/**
 * Typed API error for consumers.
 */
export class ApiClientError extends Error {
    status;
    correlationId;
    constructor(status, message, correlationId) {
        super(message);
        this.status = status;
        this.correlationId = correlationId;
        this.name = 'ApiClientError';
    }
}
/**
 * Make an authenticated API request to the auth endpoints.
 */
export async function apiRequest(endpoint, options = {}) {
    const { apiBaseUrl } = getAuthConfig();
    const url = `${apiBaseUrl}${endpoint}`;
    await ensureFreshToken();
    const { idToken } = useAuthStore.getState();
    const config = getAuthConfig();
    const correlationId = crypto.randomUUID();
    const headers = {
        'Content-Type': 'application/json',
        'X-Correlation-Id': correlationId,
        'X-Project-UserPoolId': config.cognito.userPoolId,
        'X-Project-Name': config.login.projectName,
        'X-Project-SupportEmail': config.supportEmail || 'noreply@ipsoftservizi.it',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(options.headers || {}),
    };
    const requestOptions = { ...options, headers };
    let lastError = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            const response = await fetch(url, requestOptions);
            if (response.status === 401) {
                const refreshed = await tryRefreshToken();
                if (refreshed) {
                    const newToken = useAuthStore.getState().idToken;
                    headers.Authorization = `Bearer ${newToken}`;
                    const retryResponse = await fetch(url, { ...requestOptions, headers });
                    return handleResponse(retryResponse, correlationId);
                }
                useAuthStore.getState().logout();
                throw new ApiClientError(401, 'Session expired. Please log in again.', correlationId);
            }
            if (response.status >= 500 && attempt < MAX_RETRIES - 1) {
                const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
                await sleep(delay);
                continue;
            }
            return handleResponse(response, correlationId);
        }
        catch (error) {
            if (error instanceof ApiClientError)
                throw error;
            lastError = error;
            if (attempt < MAX_RETRIES - 1) {
                const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
                await sleep(delay);
            }
        }
    }
    throw new ApiClientError(0, lastError?.message || 'Network error', correlationId);
}
async function handleResponse(response, correlationId) {
    const responseCorrelationId = response.headers.get('X-Correlation-Id') || correlationId;
    if (!response.ok) {
        let errorBody;
        try {
            errorBody = await response.json();
        }
        catch {
            errorBody = { message: response.statusText };
        }
        throw new ApiClientError(response.status, errorBody.message || response.statusText, responseCorrelationId);
    }
    const data = await response.json();
    return { data, status: response.status, correlationId: responseCorrelationId };
}
async function ensureFreshToken() {
    try {
        const session = await getCurrentSession();
        if (session) {
            const tokens = getTokensFromSession(session);
            useAuthStore.getState().setTokens(tokens.accessToken, tokens.idToken);
        }
    }
    catch { /* session expired */ }
}
async function tryRefreshToken() {
    try {
        const session = await getCurrentSession();
        if (session) {
            const tokens = getTokensFromSession(session);
            useAuthStore.getState().setTokens(tokens.accessToken, tokens.idToken);
            return true;
        }
        return false;
    }
    catch {
        return false;
    }
}
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/** Convenience methods */
export const authApi = {
    get: (endpoint) => apiRequest(endpoint, { method: 'GET' }),
    post: (endpoint, body) => apiRequest(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
    put: (endpoint, body) => apiRequest(endpoint, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
    delete: (endpoint) => apiRequest(endpoint, { method: 'DELETE' }),
};
//# sourceMappingURL=api-client.js.map